import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ScenarioSimulator = ({ baseRoute, onScenarioChange }) => {
  const [scenarios, setScenarios] = useState([]);
  const [activeScenario, setActiveScenario] = useState(null);
  const [isCreatingScenario, setIsCreatingScenario] = useState(false);
  const [newScenario, setNewScenario] = useState({
    name: '',
    speedAdjustment: '0',
    fuelType: 'vlsfo',
    routeDetour: 'none',
    weatherRouting: true
  });

  const speedOptions = [
    { value: '-2', label: 'Reduce by 2 knots (Eco Speed)' },
    { value: '-1', label: 'Reduce by 1 knot' },
    { value: '0', label: 'No change (Optimal Speed)' },
    { value: '1', label: 'Increase by 1 knot' },
    { value: '2', label: 'Increase by 2 knots (Fast Transit)' }
  ];

  const fuelTypeOptions = [
    { value: 'vlsfo', label: 'VLSFO (0.5% Sulfur)' },
    { value: 'mgo', label: 'Marine Gas Oil' },
    { value: 'lng', label: 'Liquefied Natural Gas' },
    { value: 'methanol', label: 'Green Methanol' },
    { value: 'ammonia', label: 'Green Ammonia' }
  ];

  const detourOptions = [
    { value: 'none', label: 'No detour (Direct Route)' },
    { value: 'cape-route', label: 'Cape of Good Hope Route' },
    { value: 'panama-canal', label: 'Via Panama Canal' },
    { value: 'northern-route', label: 'Northern Sea Route' },
    { value: 'custom', label: 'Custom Waypoints' }
  ];

  // Mock scenario calculations
  const calculateScenarioImpact = (scenario) => {
    const baseValues = {
      duration: 14.2,
      fuelConsumption: 1245,
      co2Emissions: 3920,
      euaCost: 196000,
      ciiRating: 'B'
    };

    let adjustments = {
      duration: 0,
      fuelConsumption: 0,
      co2Emissions: 0,
      euaCost: 0,
      ciiRating: baseValues?.ciiRating
    };

    // Speed adjustments
    const speedChange = parseInt(scenario?.speedAdjustment);
    if (speedChange !== 0) {
      adjustments.duration = -speedChange * 0.8; // Faster = shorter duration
      adjustments.fuelConsumption = speedChange * 85; // Faster = more fuel
      adjustments.co2Emissions = speedChange * 267; // More fuel = more emissions
    }

    // Fuel type adjustments
    switch (scenario?.fuelType) {
      case 'lng':
        adjustments.co2Emissions -= 780; // 20% reduction
        adjustments.fuelConsumption += 62; // Slightly more volume
        adjustments.ciiRating = 'B+';
        break;
      case 'methanol':
        adjustments.co2Emissions -= 1176; // 30% reduction
        adjustments.fuelConsumption += 125; // More volume needed
        adjustments.ciiRating = 'A';
        break;
      case 'ammonia':
        adjustments.co2Emissions -= 1960; // 50% reduction
        adjustments.fuelConsumption += 187; // Significantly more volume
        adjustments.ciiRating = 'A';
        break;
    }

    // Route detour adjustments
    switch (scenario?.routeDetour) {
      case 'cape-route':
        adjustments.duration += 3.2;
        adjustments.fuelConsumption += 285;
        adjustments.co2Emissions += 897;
        break;
      case 'panama-canal':
        adjustments.duration -= 1.8;
        adjustments.fuelConsumption -= 95;
        adjustments.co2Emissions -= 299;
        break;
      case 'northern-route':
        adjustments.duration -= 2.5;
        adjustments.fuelConsumption -= 156;
        adjustments.co2Emissions -= 491;
        break;
    }

    // Weather routing adjustment
    if (scenario?.weatherRouting) {
      adjustments.duration -= 0.4;
      adjustments.fuelConsumption -= 65;
      adjustments.co2Emissions -= 205;
    }

    // Calculate final values
    const result = {
      duration: Math.max(0.1, baseValues?.duration + adjustments?.duration),
      fuelConsumption: Math.max(1, baseValues?.fuelConsumption + adjustments?.fuelConsumption),
      co2Emissions: Math.max(1, baseValues?.co2Emissions + adjustments?.co2Emissions),
      ciiRating: adjustments?.ciiRating
    };

    result.euaCost = result?.co2Emissions * 50; // €50 per tonne CO2

    return result;
  };

  const handleCreateScenario = () => {
    if (!newScenario?.name?.trim()) return;

    const scenarioData = {
      id: `scenario-${Date.now()}`,
      name: newScenario?.name,
      ...newScenario,
      results: calculateScenarioImpact(newScenario),
      createdAt: new Date()?.toISOString()
    };

    const updatedScenarios = [...scenarios, scenarioData];
    setScenarios(updatedScenarios);
    setActiveScenario(scenarioData);
    setIsCreatingScenario(false);
    setNewScenario({
      name: '',
      speedAdjustment: '0',
      fuelType: 'vlsfo',
      routeDetour: 'none',
      weatherRouting: true
    });

    onScenarioChange(scenarioData);
  };

  const handleDeleteScenario = (scenarioId) => {
    const updatedScenarios = scenarios?.filter(s => s?.id !== scenarioId);
    setScenarios(updatedScenarios);
    
    if (activeScenario?.id === scenarioId) {
      setActiveScenario(updatedScenarios?.[0] || null);
      onScenarioChange(updatedScenarios?.[0] || null);
    }
  };

  const getImpactColor = (value, isPositive = false) => {
    if (value === 0) return 'text-muted-foreground';
    if (isPositive) {
      return value > 0 ? 'text-success' : 'text-error';
    }
    return value > 0 ? 'text-error' : 'text-success';
  };

  const formatImpact = (value, unit = '', showSign = true) => {
    if (value === 0) return `0${unit}`;
    const sign = showSign ? (value > 0 ? '+' : '') : '';
    return `${sign}${value?.toFixed(1)}${unit}`;
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-maritime-sm">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="FlaskConical" size={20} className="text-warning" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Scenario Simulator</h2>
              <p className="text-sm text-muted-foreground">"What if" analysis for voyage optimization</p>
            </div>
          </div>
          
          <Button
            onClick={() => setIsCreatingScenario(true)}
            iconName="Plus"
            iconPosition="left"
            variant="outline"
          >
            New Scenario
          </Button>
        </div>
      </div>
      <div className="p-6">
        {/* Create New Scenario Form */}
        {isCreatingScenario && (
          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-foreground mb-4">Create New Scenario</h3>
            
            <div className="space-y-4">
              <Input
                label="Scenario Name"
                placeholder="e.g., Eco Speed with LNG"
                value={newScenario?.name}
                onChange={(e) => setNewScenario({ ...newScenario, name: e?.target?.value })}
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Speed Adjustment"
                  options={speedOptions}
                  value={newScenario?.speedAdjustment}
                  onChange={(value) => setNewScenario({ ...newScenario, speedAdjustment: value })}
                />
                
                <Select
                  label="Alternative Fuel"
                  options={fuelTypeOptions}
                  value={newScenario?.fuelType}
                  onChange={(value) => setNewScenario({ ...newScenario, fuelType: value })}
                />
              </div>

              <Select
                label="Route Detour"
                options={detourOptions}
                value={newScenario?.routeDetour}
                onChange={(value) => setNewScenario({ ...newScenario, routeDetour: value })}
              />

              <label className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={newScenario?.weatherRouting}
                  onChange={(e) => setNewScenario({ ...newScenario, weatherRouting: e?.target?.checked })}
                  className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-accent"
                />
                <span className="text-foreground">Enable weather routing optimization</span>
              </label>

              <div className="flex items-center space-x-3 pt-2">
                <Button
                  onClick={handleCreateScenario}
                  disabled={!newScenario?.name?.trim()}
                  iconName="Check"
                  iconPosition="left"
                >
                  Create Scenario
                </Button>
                <Button
                  onClick={() => setIsCreatingScenario(false)}
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Scenarios List */}
        {scenarios?.length > 0 ? (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground">Saved Scenarios</h3>
            
            {scenarios?.map((scenario) => (
              <div
                key={scenario?.id}
                className={`border rounded-lg p-4 transition-maritime cursor-pointer ${
                  activeScenario?.id === scenario?.id
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
                onClick={() => {
                  setActiveScenario(scenario);
                  onScenarioChange(scenario);
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <Icon name="FlaskConical" size={16} className="text-warning" strokeWidth={2} />
                    <h4 className="font-medium text-foreground">{scenario?.name}</h4>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-muted-foreground">
                      {new Date(scenario.createdAt)?.toLocaleDateString()}
                    </span>
                    <Button
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleDeleteScenario(scenario?.id);
                      }}
                      variant="ghost"
                      size="sm"
                      className="p-1 h-auto"
                    >
                      <Icon name="Trash2" size={14} strokeWidth={2} />
                    </Button>
                  </div>
                </div>

                {/* Scenario Results */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground mb-1">Duration</div>
                    <div className="font-medium text-foreground">
                      {scenario?.results?.duration?.toFixed(1)} days
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground mb-1">Fuel</div>
                    <div className="font-medium text-foreground">
                      {Math.round(scenario?.results?.fuelConsumption)} MT
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground mb-1">CO₂ Emissions</div>
                    <div className="font-medium text-foreground">
                      {Math.round(scenario?.results?.co2Emissions)} MT
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground mb-1">EUA Cost</div>
                    <div className="font-medium text-foreground">
                      ${Math.round(scenario?.results?.euaCost)?.toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* CII Rating */}
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-muted-foreground">CII Rating:</span>
                    <span className={`text-sm font-semibold ${
                      scenario?.results?.ciiRating === 'A' ? 'text-success' : scenario?.results?.ciiRating?.startsWith('B') ? 'text-accent' :
                      scenario?.results?.ciiRating === 'C' ? 'text-warning' : 'text-error'
                    }`}>
                      {scenario?.results?.ciiRating}
                    </span>
                  </div>
                  
                  {activeScenario?.id === scenario?.id && (
                    <div className="flex items-center space-x-1 text-xs text-primary">
                      <Icon name="CheckCircle" size={14} strokeWidth={2} />
                      <span>Active</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Icon name="FlaskConical" size={48} className="text-muted-foreground mx-auto mb-4" strokeWidth={1} />
            <h3 className="text-lg font-medium text-foreground mb-2">No Scenarios Created</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create scenarios to analyze different voyage options and their impact on emissions and costs.
            </p>
            <Button
              onClick={() => setIsCreatingScenario(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Create Your First Scenario
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScenarioSimulator;